import { NgIf, NgClass, NgFor, AsyncPipe, LowerCasePipe } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartActions, CartLifeCycle, OrderTypes } from '@next/core-lib/cart';
import { NavigationRailSelectors, AgrLocationSummaryModule, AgrNotificationEmptyModule } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AgrNotificationModule } from '@next/core-lib/notification';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { WrapperSiteSidenavSelectors } from '@next/core-lib/wrapper-site-sidenav';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import { AppState, ValidationFeedback } from '../../models/app.models';
import * as AppSelectors from '../../store/selectors';
import { selectCartValidationMessages, selectHasValidationMessages } from '../../store/selectors';
import { isPresalesOrder } from '../../utils/cart';

import { CartComponent } from '../cart/cart.component';

import { AgreementComponent } from './agreement/agreement.component';
import { TotalOrderedWeightComponent } from './total-ordered-weight/total-ordered-weight.component';
import { TotalPricePerSalesPeriodsComponent } from './total-price/total-price-per-sales-period/total-price-per-sales-periods.component';
import { TotalPriceComponent } from './total-price/total-price.component';

@Component({
  selector: 'app-cart-confirmation',
  templateUrl: './cart-confirmation.component.html',
  styleUrls: ['./cart-confirmation.component.scss'],
  imports: [
    NgIf,
    NgClass,
    AgrLocationSummaryModule,
    MatDivider,
    AgrNotificationModule,
    CartComponent,
    MatProgressSpinner,
    TotalOrderedWeightComponent,
    TotalPriceComponent,
    TotalPricePerSalesPeriodsComponent,
    NgFor,
    MatIcon,
    AgreementComponent,
    MatButton,
    MatTooltip,
    AgrNotificationEmptyModule,
    AsyncPipe,
    LowerCasePipe,
    AgrI18nSharedModule,
  ],
})
export class CartConfirmationComponent implements OnDestroy {
  public cartLifeCycle: typeof CartLifeCycle = CartLifeCycle;

  public readonly destroy$: Subject<void> = new Subject<void>();
  public location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  public customer$: Observable<SelectedCustomer> = this.store.select(UserSelectors.selectCustomer);

  public cartId$: Observable<number> = this.store.select(RouterSelectors.getRouteParam('cartId')).pipe(map((cartId: string) => parseInt(cartId, 10)));

  public cart$: Observable<Cart> = this.cartId$.pipe(
    switchMap((cartId) => this.store.select(AppSelectors.selectCartWithHasRemarksValidationsSilosOrSupplements(cartId))),
    skipWhile((val) => !val),
    takeUntil(this.destroy$),
  );

  public validationMessages$: Observable<ValidationFeedback[]> = this.cartId$.pipe(
    switchMap((cartId) => this.store.select(selectCartValidationMessages(cartId, this.localeService))),
    skipWhile((val) => !val),
  );

  public hasValidationMessages$: Observable<boolean> = this.cartId$.pipe(switchMap((cartId) => this.store.select(selectHasValidationMessages(cartId))));

  public isSimulating$: Observable<boolean> = this.cart$.pipe(
    skipWhile((val) => !val),
    select((cart) => cart?.status === CartLifeCycle.simulating),
  );

  public isNotDesktopView$: Observable<boolean> = this.breakpoint.ltLg$;
  public isTabOrSmallerView$: Observable<boolean> = this.breakpoint.ltMd$;

  public agreement: FormControl = new FormControl(false);

  public isSimulationCompleted$: Observable<boolean> = this.cart$.pipe(
    skipWhile((val) => !val),
    select((cart) => cart?.status === CartLifeCycle.readyForSubmission),
  );

  public isReturnOrder$: Observable<boolean> = this.cart$.pipe(
    skipWhile((val) => !val),
    select((cart) => cart?.type === OrderTypes.returnableOrder),
  );

  readonly hasAcceptedTerms$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public isAtLeastAdvisor$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);
  public isNavigationRailOpened$: Observable<boolean> = this.store.select(NavigationRailSelectors.isNavigationRailOpen);
  public isSideNavCollapsed$: Observable<boolean> = this.store.select(WrapperSiteSidenavSelectors.selectSidenavCollapsed);
  public isSideNavOpen$: Observable<boolean> = this.store.select(WrapperSiteSidenavSelectors.isSidenavOpen);

  public isReadyForSubmission$: Observable<boolean> = combineLatest([this.isSimulationCompleted$, this.hasAcceptedTerms$, this.isAtLeastAdvisor$]).pipe(
    map(([isCartReady, agreementAccepted, isAdvisor]) => (agreementAccepted || isAdvisor) && isCartReady),
  );

  shouldShowPrice$ = combineLatest([this.isAtLeastAdvisor$, this.isReturnOrder$]).pipe(
    map(([isAtLeastAdvisor, isReturnOrder]) => isAtLeastAdvisor && !isReturnOrder),
  );

  protected readonly isPresalesOrder = isPresalesOrder;

  private isReadyForSubmissionSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    @Inject(LocaleService) private readonly localeService: LocaleService,
    @Inject(BreakpointServiceInjector) private readonly breakpoint: BreakpointService,
    @Inject(EnvironmentInjector) public readonly env: Environment,
  ) {
    this.isReadyForSubmission$.subscribe((ready) => this.isReadyForSubmissionSubject.next(ready));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public editOrder(): void {
    this.router.navigate(['/cart']);
  }

  public navigateToOverview(): void {
    this.router.navigate(['']);
  }

  public finalizeOrder(): void {
    this.cart$
      .pipe(
        take(1),
        tap((cart) => {
          this.store.dispatch(
            CartActions.patchFinalizeCartRequest({
              cartId: cart.id,
              profileId: cart.profileId,
            }),
          );
        }),
      )
      .subscribe();
  }

  public markOrderAsInstantOrder(): void {
    this.cart$
      .pipe(
        take(1),
        tap((cart) => {
          this.store.dispatch(
            CartActions.patchInstantOrderRequest({
              cartId: cart.id,
              profileId: cart.profileId,
            }),
          );
        }),
      )
      .subscribe();
  }
}
